import { isValid, fromUnixTime, getUnixTime } from 'date-fns';

import { getCookie, setCookie } from '../utils/cookie-handler';
import { getLoggedIn } from '../utils/cookies';
import { getCookieDomain } from '../utils/environment';
import { syncArtists } from './blocksoffice';

const performancesCookieName = '_bo_fav_performances';
const artistsCookieName = '_bo_fav_artists';
const favoriteDialogCookieName = '_fav_dlg';

const favoritePerformancesChangedEventName = 'favoritePerformancesChanged';
const favoriteArtistsChangedEventName = 'favoriteArtistsChanged';

function saveFavorites(name, list) {
    // 1 year expiry
    const expiry = new Date();
    expiry.setFullYear(expiry.getFullYear() + 1);

    setCookie(name, JSON.stringify(list), {
        domain: getCookieDomain(),
        path: '/',
        expires: expiry,
        secure: true,
    });

    if (name === artistsCookieName) {
        syncArtists();
    }
}

function setFavoritePerformances(list) {
    saveFavorites(performancesCookieName, list.map(item => {
        return [ item.id, getUnixTime(item.date) ];
    }));
}

function setFavoriteArtists(list) {
    saveFavorites(artistsCookieName, list);
}

function getFavoritePerformances() {
    const raw = getCookie(performancesCookieName);

    if (!raw) {
        return [];
    }

    try {
        const parsed = JSON.parse(raw);

        return parsed.map(favorite => {
            const [ id, date ] = favorite;

            return {
                id: parseInt(id, 10),
                date: fromUnixTime(date),
            }
        }).filter(favorite => {
            const { id, date } = favorite;

            return id && date && isValid(date);
        });
    } catch {
        // malformed cookie → reset to empty
        setFavoritePerformances([]);
        return [];
    }
}

function favoritePerformance(performance) {
    const { id, date } = performance;

    if (!id || !date) {
        return;
    }

    const favorites = getFavoritePerformances().filter(favorite => {
        return favorite.id !== id;
    });

    favorites.push({
        id,
        date,
    });

    setFavoritePerformances(favorites);

    dispatchFavoritePerformancesEvent('added', { id });
}

function unfavoritePerformance(performance) {
    const { id } = performance;

    if (!id) {
        return;
    }

    const favorites = getFavoritePerformances().filter(favorite => {
        return favorite.id !== id;
    });

    setFavoritePerformances(favorites);

    dispatchFavoritePerformancesEvent('removed', { id });
}

function isFavoritePerformance(performance) {
    const { id } = performance;

    if (!id) {
        return false;
    }

    return getFavoritePerformances().some(favorite => {
        return favorite.id === id;
    });
}

function dispatchFavoritePerformancesEvent(change, item) {
    window.dispatchEvent(new CustomEvent(favoritePerformancesChangedEventName, {
        detail: {
            type: 'performances',
            change,
            item,
            favorites: getFavoritePerformances(),
        },
    }));
}

function getFavoriteArtists() {
    const raw = getCookie(artistsCookieName);

    if (!raw) {
        return [];
    }

    try {
        const parsed = JSON.parse(raw);

        return parsed.filter(artist => {
            return artist;
        });
    } catch {
        // malformed cookie → reset to empty
        setFavoriteArtists([]);
        return [];
    }
}

function favoriteArtist(artist) {
    const { id } = artist;

    if (!id) {
        return;
    }

    const favorites = getFavoriteArtists().filter(favorite => {
        return favorite !== id;
    });

    favorites.push(id);

    setFavoriteArtists(favorites);

    dispatchFavoriteArtistsEvent('added', { id });
}

function unfavoriteArtist(artist) {
    const { id } = artist;

    if (!id) {
        return;
    }

    const favorites = getFavoriteArtists().filter(favorite => {
        return favorite !== id;
    });

    setFavoriteArtists(favorites);

    dispatchFavoriteArtistsEvent('removed', { id });
}

function isFavoriteArtist(artist) {
    const { id } = artist;

    if (!id) {
        return false;
    }

    return getFavoriteArtists().some(favorite => {
        return favorite === id;
    });
}

function dispatchFavoriteArtistsEvent(change, item) {
    window.dispatchEvent(new CustomEvent(favoriteArtistsChangedEventName, {
        detail: {
            type: 'artists',
            change,
            item,
            favorites: getFavoriteArtists(),
        },
    }));
}

function isDialogShown() {
    return getLoggedIn() || parseInt(getCookie(favoriteDialogCookieName), 10) === 1;
}

function setDialogShown() {
    setCookie(favoriteDialogCookieName, 1, {
        path: '/',
    });
}

export default {
    favoritePerformancesChangedEventName,
    favoriteArtistsChangedEventName,

    getFavoritePerformances,
    favoritePerformance,
    unfavoritePerformance,
    isFavoritePerformance,

    getFavoriteArtists,
    favoriteArtist,
    unfavoriteArtist,
    isFavoriteArtist,

    isDialogShown,
    setDialogShown,
}
